import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { min, max, step, purchaseLimit, productCartQuantity, lineQuantity } = this.getCurrentValues();
    if (purchaseLimit === null) return max;

    const otherProductQuantity = Math.max(productCartQuantity - lineQuantity, 0);
    const purchaseLimitMaximum = purchaseLimit - otherProductQuantity;
    const combinedMaximum = max === null ? purchaseLimitMaximum : Math.min(max, purchaseLimitMaximum);

    if (combinedMaximum < min) return 0;

    return min + Math.floor((combinedMaximum - min) / Math.max(step, 1)) * Math.max(step, 1);
  }

  /**
   * Updates an over-limit cart line without using the JBG maximum as a decrement floor.
   * Existing over-limit quantities must be reduced by the customer one native step at a time.
   *
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { purchaseLimit, value } = this.getCurrentValues();

    if (purchaseLimit === null) {
      super.updateQuantity(stepMultiplier);
      return;
    }

    const effectiveMax = this.getEffectiveMax();

    if (effectiveMax === null || value <= effectiveMax) {
      super.updateQuantity(stepMultiplier);
      return;
    }

    if (stepMultiplier >= 0) {
      this.updateButtonStates();
      return;
    }

    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const newValue = Math.max(min, value + step * stepMultiplier);

    if (newValue === value) {
      this.updateButtonStates();
      return;
    }

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Validates a typed limited-cart edit against the confirmed line quantity.
   * A reduction may remain above the JBG maximum, but an increase may not exceed it.
   *
   * @param {Event} event - The input blur event
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const { quantityInput } = this.refs;
    const { min, step, purchaseLimit, lineQuantity } = this.getCurrentValues();

    if (purchaseLimit === null || !quantityInput.hasAttribute('data-line-quantity')) {
      super.setQuantity(event);
      return;
    }

    const effectiveMax = this.getEffectiveMax();

    if (effectiveMax === null || lineQuantity <= effectiveMax) {
      super.setQuantity(event);
      return;
    }

    event.preventDefault();
    const parsedQuantity = Math.max(min, parseInt(event.target.value) || 0);
    const isReduction = parsedQuantity < lineQuantity;
    const exceedsEffectiveMax = effectiveMax !== null && parsedQuantity > effectiveMax;
    const isValidIncrement = (parsedQuantity - min) % step === 0;

    if (!isValidIncrement || (!isReduction && exceedsEffectiveMax)) {
      quantityInput.value = lineQuantity.toString();
      this.updateButtonStates();
      return;
    }

    quantityInput.value = parsedQuantity.toString();

    if (parsedQuantity !== lineQuantity) {
      this.onQuantityChange();
    }

    this.updateButtonStates();
  }

  /**
   * Restores the authoritative cart-line quantity without clamping it to the JBG maximum.
   *
   * Unlimited selectors retain Horizon's inherited synchronization behavior.
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { purchaseLimit, lineQuantity } = this.getCurrentValues();

    if (purchaseLimit === null || !quantityInput.hasAttribute('data-line-quantity')) {
      super.updateCartQuantity();
      return;
    }

    quantityInput.value = lineQuantity.toString();
    this.updateButtonStates();
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
